import { inject } from '@angular/core';
import { signalStore, signalStoreFeature, withState } from '@ngrx/signals';
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { Events, withEventHandlers, withReducer } from '@ngrx/signals/events';
import { immerOn } from 'ngrx-immer/signals';
import { EMPTY, switchMap, tap } from 'rxjs';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { AUTH_SERVICE, authEvents, SessionUser } from '@kikkz/shared/auth/util';

export interface AuthState {
  refreshToken: string | null;
  accessToken: string | null;
  idToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isRefreshing: boolean;
  expiresAt: string | null;
  hasError: boolean;
  belongsToAdRestrictedGroup: boolean;
  userName: string | null;
  accessDenied: boolean | null;
  // Optional profile snapshot supplied by a tenant's session-auth payload (e.g. after an OAuth
  // exchange). Tenant-neutral core never derives this itself.
  sessionUser: SessionUser | null;
}

export const initialAuthState: AuthState = {
  refreshToken: null,
  accessToken: null,
  idToken: null,
  isAuthenticated: false,
  isLoading: false,
  isRefreshing: false,
  expiresAt: null,
  hasError: false,
  belongsToAdRestrictedGroup: false,
  userName: null,
  accessDenied: null,
  sessionUser: null,
};

/**
 * Tenant-neutral auth core: app-init lifecycle, generic session materialization
 * (`setSessionAuth`), and logout. Tenant-specific login flows
 * (e.g. OAuth/PKCE) are layered on top via their own signal-store features and
 * dispatch `setSessionAuth` once they've obtained a session.
 */
export function withAuthCore() {
  return signalStoreFeature(
    withState(initialAuthState),
    withReducer(
      immerOn(authEvents.initAuth, (state) => {
        state.isLoading = true;
        state.hasError = false;
      }),
      immerOn(authEvents.setSessionAuth, (state, { payload }) => {
        state.idToken = payload.idToken;
        state.accessToken = payload.accessToken ?? payload.idToken;
        state.refreshToken = null;
        state.expiresAt = payload.expiresAt ?? expiresAtFromIdToken(payload.idToken);
        state.isAuthenticated = true;
        state.isLoading = false;
        state.hasError = false;
        state.userName = payload.userName ?? null;
        state.accessDenied = payload.accessDenied ?? null;
        state.belongsToAdRestrictedGroup = payload.belongsToAdRestrictedGroup ?? false;
        state.sessionUser = payload.sessionUser ?? null;
      }),
      immerOn(authEvents.logout, (state) => {
        Object.assign(state, { ...initialAuthState });
      }),
    ),
    withEventHandlers((_, events = inject(Events)) => {
      const authService = inject(AUTH_SERVICE);

      return {
        // --- General ---
        logout$: events.on(authEvents.logout).pipe(
          tap(() => authService.logout()),
          switchMap(() => EMPTY),
        ),
      };
    }),
  );
}

export const AuthStore = signalStore(withDevtools('auth'), withAuthCore());

// Derive an ISO expiry from the id_token's own `exp` claim (far-future fallback).
// Tenant-neutral: only reads the generic JWT `exp` claim, no tenant-specific payload shape.
function expiresAtFromIdToken(idToken: string): string {
  try {
    const exp = jwtDecode<JwtPayload>(idToken).exp;
    if (typeof exp === 'number') return new Date(exp * 1000).toISOString();
  } catch {
    // ignore — fall through to far-future fallback
  }
  return new Date(8640000000000000).toISOString();
}
